import { useState, useEffect } from "react";
import { X, Search } from "lucide-react";
import { LoadingSpinner } from "../LoadingSpinner";
import { useTemplates } from "../../hooks/useTemplates";
import { useClickOutside } from "../../hooks/useClickOutside";
import useEscapeKey from "../../hooks/useEscapeKey";
import { useProjectContext } from "../../contexts/ProjectContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { Template } from "../../types";
import { motion } from "framer-motion";
import { saveProjectId } from "../../store/persistence";

interface TemplateDialogProps {
  onClose: () => void;
}

export default function TemplateDialog({ onClose }: TemplateDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);
  const { templates, isLoading, loadTemplates } = useTemplates();
  const { loadFromTemplate } = useProjectContext();
  const { showNotification } = useNotifications();

  const dialogRef = useClickOutside<HTMLDivElement>(onClose);
  useEscapeKey(onClose); // Use escape key to close the dialog

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" ||
      template.category.toLowerCase() === selectedCategory.toLowerCase();

    return matchesSearch && matchesCategory;
  });

  const categories = [
    "all",
    ...new Set(templates.map((t) => t.category.toLowerCase())),
  ];

  const handleTemplateSelect = async (template: Template) => {
    try {
      setLoadingTemplateId(template.id);
      
      await loadFromTemplate(template.id);
      
      // Clear the saved project ID since we're loading a template
      // This ensures that when the page is refreshed, it doesn't try to load the previous project
      saveProjectId(null);
      
      showNotification("success", "Template loaded successfully");
      onClose();
    } catch (error) {
      showNotification("error", "Failed to load template");
      setLoadingTemplateId(null);
    }
  };


  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
          <motion.div
            ref={dialogRef}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl mx-4 border border-gray-200 dark:border-gray-700 max-h-[90vh] flex flex-col"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Header */}
            <div className="px-6 pt-5 rounded-t-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Browse Templates
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Search and filters */}
              <div className="space-y-4 pb-4 border-b border-gray-200 dark:border-gray-800">
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                </div>

                <div className="flex gap-2 overflow-x-auto pb-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`
                    px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap
                    ${
                      selectedCategory === category
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }
                  `}
                    >
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Template Grid */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-500 dark:text-gray-400">Loading templates...</p>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No templates found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      disabled={loadingTemplateId === template.id}
                      className={`flex flex-col text-left rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 overflow-hidden transition-all ${
                        loadingTemplateId === template.id ? 'opacity-70 cursor-wait' : ''
                      }`}
                    >
                      <img
                        src={template.preview.startsWith('http') ? template.preview : `/templates/${template.preview}`}
                        alt=""
                        className="w-full h-40 object-cover"
                      />
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                            {template.category}
                          </span>
                        </div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
                          {template.name}
                          {loadingTemplateId === template.id && (
                            <LoadingSpinner size="sm" className="text-blue-500" />
                          )}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                          {template.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 rounded-b-lg">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredTemplates.length} template
                  {filteredTemplates.length !== 1 ? "s" : ""} found
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
    </motion.div>
  );
}
